import { DestroyRef, inject, signal, Signal } from '@angular/core';

/**
 * A signal tracking whether `query` currently matches, updated on every change.
 *
 * Without `window.matchMedia` (server-side rendering, tests) the signal stays at
 * `false` and never changes.
 *
 * Calls `inject()`, so — like `viewChild()` — this must be invoked directly in a
 * class field initializer or a constructor, not inside a callback.
 *
 * @param {string} query - The media query to track, e.g. `'(pointer: coarse)'`
 * @returns {Signal<boolean>} Whether the query currently matches
 */
export function injectMediaQueryMatch(query: string): Signal<boolean> {
  const matches = signal<boolean>(false);

  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return matches.asReadonly();
  }

  const mediaQuery = window.matchMedia(query);
  matches.set(mediaQuery.matches);

  const listener = (event: MediaQueryListEvent) => matches.set(event.matches);
  mediaQuery.addEventListener('change', listener);
  inject(DestroyRef).onDestroy(() => mediaQuery.removeEventListener('change', listener));

  return matches.asReadonly();
}
